import React, { useState, useEffect } from 'react';
import { contacts, getAuthHeader, handleResponse, API_BASE_URL } from '../../../utils/api';

export function SelectAuctionHouseModal({ isOpen, onClose, onSubmit, systemAuctionHouseName, systemCurrency }) {
  const [selectedContactId, setSelectedContactId] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(systemCurrency || 'USD');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newContactName, setNewContactName] = useState(systemAuctionHouseName || '');
  const [website, setWebsite] = useState('');
  const [contactsList, setContactsList] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      // Fetch contacts and currencies when modal opens
      const fetchData = async () => {
        try {
      const [contactsRes, currenciesRes] = await Promise.all([
        contacts.getAll(),
        fetch(`${API_BASE_URL}/currencies`, {
          headers: await getAuthHeader('GET'),
          credentials: 'include'
        }).then(handleResponse)
      ]);
      setContactsList(contactsRes);
      setCurrencies(currenciesRes);
          setIsLoading(false);
        } catch (error) {
          console.error('Failed to fetch data:', error);
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ 
      contactId: parseInt(selectedContactId),
      currency: selectedCurrency
    });
    onClose();
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    try {
      const result = await contacts.create({
        company_name: newContactName,
        website,
        contact_type: 'auction_house'
      });
      onSubmit({ 
        contactId: result.id,
        currency: selectedCurrency
      });
      onClose();
    } catch (error) {
      console.error('Failed to create contact:', error);
    }
  };

  if (!isOpen) return null;

  const filteredContacts = contactsList?.filter(c => c.contact_type === 'auction_house') || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          Add Auction to Planner
        </h3>

        {!showCreateForm ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auction House
              </label>
              <select
                value={selectedContactId}
                onChange={(e) => setSelectedContactId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                <option value="">Select an auction house...</option>
                {filteredContacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Currency
              </label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                {currencies?.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Create New Auction House
              </button>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedContactId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Add to Planner
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleCreateContact}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
              </label>
              <input
                type="text"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="Enter company name"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="https://example.com"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Currency
              </label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                {currencies?.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!newContactName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Create & Add
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
